import React, { Fragment, useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import Layout from '../../components/Layout/Layout';
import Seo from '../../components/Seo';
import { toast } from 'react-toastify';
import NotFoundPage from './NotFoundPage';
import SoftwareBoxMockup from '../../components/HomeProducts/SoftwareBoxMockup';

const API_URL = process.env.REACT_APP_API_URL || 'https://orbitronix.ge/api/';

const ProductDetailsSkeleton = () => (
    <Layout>
        <main className="page_content pt-150 pb-120">
            <div className="container">
                <div style={{ maxWidth: '800px', margin: '0 auto', opacity: 0.6 }}>
                    <div style={{ height: '36px', background: 'rgba(255,255,255,0.08)', borderRadius: '8px', marginBottom: '20px', width: '60%' }} />
                    <div style={{ height: '24px', background: 'rgba(255,255,255,0.05)', borderRadius: '6px', marginBottom: '40px', width: '80%' }} />
                    <div style={{ height: '300px', background: 'rgba(255,255,255,0.04)', borderRadius: '16px' }} />
                </div>
            </div>
        </main>
    </Layout>
);

const ProductDetails = () => {
    const { slug, lang: urlLang } = useParams();
    const navigate = useNavigate();
    const { i18n } = useTranslation();
    const [product, setProduct] = useState(null);
    const [related, setRelated] = useState([]);
    const [allCatalogProducts, setAllCatalogProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);
    const lang = urlLang || i18n.language || 'en';

    const sliderRef = useRef(null);

    const scrollSlider = useCallback((dir) => {
        if (!sliderRef.current) return;
        const slide = sliderRef.current.querySelector('.pd-slide-item');
        const step = slide ? slide.offsetWidth + 24 : 360;
        sliderRef.current.scrollBy({ left: step * dir, behavior: 'smooth' });
    }, []);


    useEffect(() => {
        if (urlLang && (urlLang === 'en' || urlLang === 'ka')) {
            i18n.changeLanguage(urlLang);
        }
    }, [urlLang, i18n]);

    useEffect(() => {
        if (!slug || slug === 'undefined' || slug === 'null') {
            setNotFound(true);
            setLoading(false);
            return;
        }

        const fetchProduct = async () => {
            setLoading(true);
            try {
                const [res, allRes] = await Promise.all([
                    fetch(`${API_URL}products.php?slug=${slug}`).catch(() => null),
                    fetch(`${API_URL}products.php`).catch(() => null)
                ]);

                if (allRes && allRes.ok) {
                    const allData = await allRes.json();
                    if (Array.isArray(allData)) {
                        setAllCatalogProducts(allData);
                    }
                }

                if (res && res.ok) {
                    const data = await res.json();
                    if (data && !data.error && data.id) {
                        setProduct(data);
                        setNotFound(false);

                        // Fetch related products in the same category
                        if (data.category_id) {
                            try {
                                const relRes = await fetch(`${API_URL}products.php?category_id=${data.category_id}`);
                                const relData = await relRes.json();
                                if (Array.isArray(relData)) {
                                    setRelated(relData.filter(p => p.id !== data.id).slice(0, 3));
                                }
                            } catch (e) {
                                console.error('Error fetching related products:', e);
                            }
                        }
                    } else {
                        setNotFound(true);
                    }
                } else {
                    setNotFound(true);
                }
            } catch (err) {
                console.error('Error fetching product:', err);
                setNotFound(true);
            }
            setLoading(false);
            window.scrollTo(0, 0);
        };

        fetchProduct();
    }, [slug, lang]);

    // useMemo must be BEFORE any early returns (Rules of Hooks)
    const filteredCatalog = useMemo(() => {
        let list = allCatalogProducts.filter(p => p.id !== product?.id && p.slug !== slug);
        list.sort((a, b) => (b.is_featured || 0) - (a.is_featured || 0));
        return list;
    }, [allCatalogProducts, product, slug]);

    if (notFound || !slug || slug === 'undefined' || slug === 'null') {
        return <NotFoundPage />;
    }

    if (loading) return <ProductDetailsSkeleton />;
    if (!product) return <NotFoundPage />;

    const productName = lang === 'ka' ? product.name_ka : (product.name_en || product.name_ka);
    const productTagline = lang === 'ka' ? product.tagline_ka : (product.tagline_en || product.tagline_ka);
    const productDesc = lang === 'ka' ? product.description_ka : (product.description_en || product.description_ka);
    const features = (lang === 'ka' ? product.features_ka : (product.features_en && product.features_en.length > 0 ? product.features_en : product.features_ka)) || [];
    const categoryName = lang === 'ka' ? product.category_name_ka : (product.category_name_en || product.category_name_ka);
    const accentColor = product.badge_color || '#6c5ce7';

    const getPricingModelLabel = (model) => {
        if (lang === 'ka') {
            switch (model) {
                case 'one_time': return 'ერთჯერადი ლიცენზია';
                case 'subscription': return 'თვიური / წლიური გამოწერა';
                case 'one_time_plus_support': return 'ლიცენზია + ყოველწლიური მხარდაჭერა (ARR)';
                case 'quote': return 'ინდივიდუალური ფასი მოთხოვნით';
                default: return 'ლიცენზია + მხარდაჭერა';
            }
        }
        switch (model) {
            case 'one_time': return 'One-time License';
            case 'subscription': return 'Subscription (SaaS)';
            case 'one_time_plus_support': return 'License + Annual Support (ARR)';
            case 'quote': return 'Custom Quote on Request';
            default: return 'Commercial License';
        }
    };

    const getMarketLabel = (market) => {
        if (lang === 'ka') {
            switch (market) {
                case 'municipal': return '🏛️ მუნიციპალური მმართველობა';
                case 'government': return '🏢 სამთავრობო უწყებები';
                case 'business': return '💼 კორპორატიული & ბიზნეს სექტორი';
                case 'personal': return '👤 პირადი / საჯარო სერვისები';
                case 'education': return '🎓 საგანმანათლებლო ინსტიტუტები';
                default: return '🌍 ყველა სეგმენტი';
            }
        }
        switch (market) {
            case 'municipal': return '🏛️ Municipalities';
            case 'government': return '🏢 Government Agencies';
            case 'business': return '💼 Corporate & Enterprise';
            case 'personal': return '👤 Citizen & Public Services';
            case 'education': return '🎓 Educational Institutions';
            default: return '🌍 Enterprise / All';
        }
    };

    const copyLink = () => {
        navigator.clipboard.writeText(window.location.href);
        toast.success(lang === 'ka' ? 'ბმული დაკოპირდა!' : 'Link copied to clipboard!');
    };



    return (
        <Fragment>
            <Seo
                title={`${productName} | Orbitronix Products`}
                description={productTagline || productDesc || 'Orbitronix software solution'}
                keywords={`orbitronix, ${productName}, software, municipal solutions, digital governance`}
                url={`/${lang}/products/${slug}`}
            />
            <Layout>
                <main className="page_content pt-140 pb-120">
                    <div className="container">
                        {/* Breadcrumbs */}
                        <div className="pd-breadcrumbs mb-4">
                            <Link to={`/${lang}`}>{lang === 'ka' ? 'მთავარი' : 'Home'}</Link>
                            <span className="pd-sep">/</span>
                            <Link to={`/${lang}/products`}>{lang === 'ka' ? 'პროდუქტები' : 'Products'}</Link>
                            {categoryName && (
                                <>
                                    <span className="pd-sep">/</span>
                                    <span>{categoryName}</span>
                                </>
                            )}
                            <span className="pd-sep">/</span>
                            <span className="pd-current">{productName}</span>
                        </div>

                        {/* Top Hero Banner */}
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5 }}
                            className="pd-hero-banner mb-5"
                            style={{
                                '--accent': accentColor,
                                borderLeft: `6px solid ${accentColor}`
                            }}
                        >
                            <div className="row align-items-center g-4">
                                <div className="col-lg-8">
                                    <div className="d-flex align-items-center gap-3 mb-3 flex-wrap">
                                        <div 
                                            className="pd-icon-box"
                                            style={{ background: `${accentColor}20`, borderColor: `${accentColor}40` }}
                                        >
                                            {product.icon || '📦'}
                                        </div>
                                        <div>
                                            <div className="d-flex gap-2 align-items-center flex-wrap">
                                                {categoryName && (
                                                    <span className="pd-badge-category">
                                                        {product.category_icon || '📁'} {categoryName}
                                                    </span>
                                                )}
                                                <span className="pd-badge-market">
                                                    {getMarketLabel(product.target_market)}
                                                </span>
                                                {product.is_featured === 1 && (
                                                    <span className="pd-badge-featured">⭐ Featured</span>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    <h1 className="pd-title text-white fw-bold mb-2">{productName}</h1>
                                    {productTagline && (
                                        <p className="pd-tagline">{productTagline}</p>
                                    )}
                                </div>

                                <div className="col-lg-4 text-lg-end">
                                    <div className="d-flex gap-2 justify-content-lg-end flex-wrap align-items-center">
                                        <Link 
                                            to={`/${lang}/contact?product=${encodeURIComponent(productName)}`} 
                                            className="pd-btn-order"
                                            style={{ 
                                                textDecoration: 'none', 
                                                display: 'inline-flex', 
                                                alignItems: 'center', 
                                                padding: '9px 18px', 
                                                borderRadius: '8px',
                                                background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
                                                borderColor: '#818cf8',
                                                color: '#ffffff',
                                                fontWeight: '600',
                                                fontSize: '13.5px'
                                            }}
                                        >
                                            <i className="fas fa-paper-plane me-2" />
                                            {lang === 'ka' ? 'დემო / შეკვეთა' : 'Request Demo / Quote'}
                                        </Link>
                                        <button onClick={copyLink} className="pd-btn-share" title="Copy URL">
                                            <i className="fas fa-share-alt me-2" />
                                            {lang === 'ka' ? 'გაზიარება' : 'Share'}
                                        </button>
                                        {product.demo_url && (
                                            <a 
                                                href={product.demo_url} 
                                                target="_blank" 
                                                rel="noopener noreferrer" 
                                                className="pd-btn-live-demo"
                                            >
                                                <i className="fas fa-external-link-alt me-2" />
                                                Live Demo
                                            </a>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </motion.div>

                        {/* Main Grid: Content & Sidebar */}
                        <div className="row g-4">
                            {/* Left: Product Information */}
                            <div className="col-lg-8">
                                {/* Description Card */}
                                <div className="pd-content-card mb-4">
                                    <h3 className="pd-section-heading">
                                        <i className="fas fa-info-circle me-2" style={{ color: accentColor }} />
                                        {lang === 'ka' ? 'პროდუქტის აღწერა და დანიშნულება' : 'Product Overview'}
                                    </h3>
                                    <div className="pd-description">
                                        <p>{productDesc || productTagline}</p>
                                    </div>
                                </div>

                                {/* Features List Card */}
                                {features.length > 0 && (
                                    <div className="pd-content-card mb-4">
                                        <h3 className="pd-section-heading">
                                            <i className="fas fa-check-double me-2" style={{ color: accentColor }} />
                                            {lang === 'ka' ? 'ძირითადი ფუნქციონალი და მახასიათებლები' : 'Core Capabilities & Features'}
                                        </h3>
                                        <div className="pd-features-grid">
                                            {features.map((feature, idx) => (
                                                <div key={idx} className="pd-feature-box">
                                                    <div 
                                                        className="pd-feature-check"
                                                        style={{ background: `${accentColor}20`, color: accentColor }}
                                                    >
                                                        <i className="fas fa-check" />
                                                    </div>
                                                    <span className="pd-feature-text">{feature}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Technical Stack */}
                                {product.tech_stack && (
                                    <div className="pd-content-card mb-4">
                                        <h3 className="pd-section-heading">
                                            <i className="fas fa-layer-group me-2" style={{ color: accentColor }} />
                                            {lang === 'ka' ? 'ტექნოლოგიური სტეკი & თავსებადობა' : 'Technology Stack & Compatibility'}
                                        </h3>
                                        <div className="pd-tech-tags">
                                            {product.tech_stack.split(',').map((tech, i) => (
                                                <span key={i} className="pd-tech-pill">
                                                    <i className="fas fa-cube me-1 text-primary" />
                                                    {tech.trim()}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Standards & Security Guarantee */}
                                <div className="pd-content-card mb-4">
                                    <h3 className="pd-section-heading">
                                        <i className="fas fa-shield-alt me-2" style={{ color: accentColor }} />
                                        {lang === 'ka' ? 'უსაფრთხოება, სტანდარტები და მხარდაჭერა' : 'Standards, Security & SLA'}
                                    </h3>
                                    <div className="row g-3 mt-1">
                                        <div className="col-md-6">
                                            <div className="pd-assurance-item">
                                                <span className="pd-assurance-icon">🔒</span>
                                                <div>
                                                    <strong>{lang === 'ka' ? 'WCAG 2.1 AA & ISO სტანდარტები' : 'WCAG 2.1 AA & ISO Aligned'}</strong>
                                                    <p>{lang === 'ka' ? 'სრული თავსებადობა სახელმწიფო და საერთაშორისო ციფრულ სტანდარტებთან.' : 'Full compliance with state and international accessibility and security standards.'}</p>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-md-6">
                                            <div className="pd-assurance-item">
                                                <span className="pd-assurance-icon">⚡</span>
                                                <div>
                                                    <strong>{lang === 'ka' ? '24/7 SLA მონიტორინგი & სარეზერვო კოპირება' : '24/7 Monitoring & Automated Backups'}</strong>
                                                    <p>{lang === 'ka' ? 'გარანტირებული 99.9% uptime და მონაცემთა უსაფრთხოების რეგულარული აუდიტი.' : 'Guaranteed 99.9% uptime with automated encrypted off-site backups.'}</p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Right: Pricing & Order Sidebar */}
                            <div className="col-lg-4">
                                <div className="pd-sidebar-sticky">
                                    {/* 3D Product Box Mockup (Cover) */}
                                    <div className="pd-cover-showcase mb-4 p-3 text-center rounded-3" style={{ background: '#080b1a', border: '1px solid rgba(255, 255, 255, 0.08)', boxShadow: '0 12px 36px rgba(0, 0, 0, 0.4)' }}>
                                        <SoftwareBoxMockup 
                                            product={product} 
                                            lang={lang} 
                                        />
                                    </div>

                                    {/* Pricing Box */}
                                    <div className="pd-pricing-card mb-4">
                                        <div className="pd-pricing-badge">
                                            {getPricingModelLabel(product.pricing_model)}
                                        </div>

                                        <div className="pd-price-row mb-3">
                                            <span className="pd-price-type">
                                                {lang === 'ka' ? 'დანერგვის / საწყისი საფასური' : 'Setup & Deployment Price'}
                                            </span>
                                            <div className="pd-price-large">
                                                {parseFloat(product.setup_price) > 0 ? (
                                                    <>
                                                        <span className="pd-currency">₾</span>
                                                        {parseFloat(product.setup_price).toLocaleString()}
                                                    </>
                                                ) : (
                                                    <span className="pd-price-quote">{lang === 'ka' ? 'ინდივიდუალური' : 'On Request'}</span>
                                                )}
                                            </div>
                                        </div>

                                        {parseFloat(product.annual_price) > 0 && (
                                            <div className="pd-price-sub-row mb-3">
                                                <span className="text-white-50 small">
                                                    {lang === 'ka' ? 'წლიური მხარდაჭერა / ARR:' : 'Annual Support / ARR:'}
                                                </span>
                                                <span className="fw-bold text-success">
                                                    ₾{parseFloat(product.annual_price).toLocaleString()} / {lang === 'ka' ? 'წელი' : 'yr'}
                                                </span>
                                            </div>
                                        )}

                                        {parseFloat(product.monthly_price) > 0 && (
                                            <div className="pd-price-sub-row mb-3">
                                                <span className="text-white-50 small">
                                                    {lang === 'ka' ? 'თვიური საფასური:' : 'Monthly:'}
                                                </span>
                                                <span className="fw-bold text-info">
                                                    ₾{parseFloat(product.monthly_price).toLocaleString()} / {lang === 'ka' ? 'თვე' : 'mo'}
                                                </span>
                                            </div>
                                        )}

                                        <div className="pd-divider my-3" />

                                        {/* Contact / Order CTA */}
                                        <Link 
                                            to={`/${lang}/contact?product=${encodeURIComponent(productName)}`} 
                                            className="pd-btn-order w-100 text-center mb-2"
                                            title={lang === 'ka' ? 'მოითხოვეთ დემო / შეკვეთა' : 'Request Demo / Quote'}
                                        >
                                            <i className="fas fa-paper-plane me-2" />
                                            {lang === 'ka' ? 'მოითხოვეთ დემო / შეკვეთა' : 'Request Demo / Quote'}
                                        </Link>

                                        {product.docs_url && (
                                            <a 
                                                href={product.docs_url} 
                                                target="_blank" 
                                                rel="noopener noreferrer" 
                                                className="pd-btn-docs w-100 text-center mb-3"
                                                title={lang === 'ka' ? 'პროდუქტის დოკუმენტაცია' : 'Product Documentation'}
                                            >
                                                <i className="fas fa-book me-2" />
                                                {lang === 'ka' ? 'დოკუმენტაცია' : 'Documentation'}
                                            </a>
                                        )}

                                        <div className="pd-guarantee-list">
                                            <div className="pd-g-item">
                                                <i className="fas fa-check text-success me-2" />
                                                {lang === 'ka' ? 'ოფიციალური კონტრაქტი & ინვოისი' : 'Official Contract & Invoicing'}
                                            </div>
                                            <div className="pd-g-item">
                                                <i className="fas fa-check text-success me-2" />
                                                {lang === 'ka' ? 'პერსონალის ტრენინგი & ინსტრუქტაჟი' : 'Staff Onboarding & Training'}
                                            </div>
                                            <div className="pd-g-item">
                                                <i className="fas fa-check text-success me-2" />
                                                {lang === 'ka' ? 'სატელეფონო & ონლაინ მხარდაჭერა' : 'Direct Phone & Ticket Support'}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Related Products */}
                                    {related.length > 0 && (
                                        <div className="pd-related-card">
                                            <h4 className="pd-related-title mb-3">
                                                {lang === 'ka' ? 'მსგავსი გადაწყვეტილებები' : 'Related Solutions'}
                                            </h4>
                                            <div className="d-flex flex-column gap-2">
                                                {related.map(rel => (
                                                    <Link 
                                                        key={rel.id} 
                                                        to={`/${lang}/products/${rel.slug || rel.id}`}
                                                        className="pd-related-item"
                                                        title={lang === 'ka' ? rel.name_ka : (rel.name_en || rel.name_ka)}
                                                    >
                                                        <span className="pd-related-icon">{rel.icon || '📦'}</span>
                                                        <div className="overflow-hidden">
                                                            <div className="pd-related-name text-truncate">
                                                                {lang === 'ka' ? rel.name_ka : (rel.name_en || rel.name_ka)}
                                                            </div>
                                                            {parseFloat(rel.setup_price) > 0 && (
                                                                <small className="text-white-50">
                                                                    ₾{parseFloat(rel.setup_price).toLocaleString()}
                                                                </small>
                                                            )}
                                                        </div>
                                                    </Link>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Listed Other Products as 3D Cover Slider */}
                        {filteredCatalog.length > 0 && (
                            <div className="pd-explore-catalog-section mt-80 pt-60 border-top border-secondary border-opacity-25">
                                <div className="d-flex align-items-center justify-content-between flex-wrap gap-3 mb-4">
                                    <div>
                                        <span className="pd-explore-sub text-uppercase tracking-wider">
                                            <i className="fas fa-boxes me-2 text-primary" />
                                            {lang === 'ka' ? 'Orbitronix პროგრამული ეკოსისტემა' : 'Orbitronix Software Ecosystem'}
                                        </span>
                                        <h3 className="pd-explore-title text-white mt-1 mb-0">
                                            {lang === 'ka' ? 'სხვა ციფრული გადაწყვეტილებები & პლატფორმები' : 'Explore Other Enterprise Solutions'}
                                        </h3>
                                    </div>

                                    {/* Slider Controls */}
                                    <div className="d-flex align-items-center gap-2">
                                        <Link to={`/${lang}/products`} className="pd-all-catalog-btn" title={lang === 'ka' ? 'სრული კატალოგი' : 'View Full Products Catalog'}>
                                            <span>{lang === 'ka' ? 'სრული კატალოგი' : 'View Catalog'}</span>
                                            <i className="fas fa-arrow-right ms-1" />
                                        </Link>

                                        {/* Slider Arrows */}
                                        <div className="d-flex align-items-center gap-1 ms-1">
                                            <button 
                                                type="button" 
                                                className="pd-slider-nav-btn" 
                                                onClick={() => scrollSlider(-1)}
                                                aria-label="Previous"
                                                title={lang === 'ka' ? 'წინა' : 'Previous'}
                                            >
                                                <i className="fas fa-chevron-left" />
                                            </button>
                                            <button 
                                                type="button" 
                                                className="pd-slider-nav-btn" 
                                                onClick={() => scrollSlider(1)}
                                                aria-label="Next"
                                                title={lang === 'ka' ? 'შემდეგი' : 'Next'}
                                            >
                                                <i className="fas fa-chevron-right" />
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                {/* Products Slider */}
                                <div className="pd-slider-wrapper position-relative">
                                    <div className="pd-slider-track" ref={sliderRef}>
                                        {filteredCatalog.map((item, idx) => {
                                            const itemName = lang === 'ka' ? item.name_ka : (item.name_en || item.name_ka);
                                            const itemTagline = lang === 'ka' ? item.tagline_ka : (item.tagline_en || item.tagline_ka);
                                            const itemAccent = item.badge_color || '#6c5ce7';
                                            const itemUrl = `/${lang}/products/${item.slug || item.id}`;

                                            return (
                                                <div className="pd-slide-item" key={item.id || idx}>
                                                    <div 
                                                        className="pd-card-item h-100"
                                                        style={{ '--card-accent': itemAccent, borderTop: `4px solid ${itemAccent}` }}
                                                    >
                                                        {/* 3D Product Box Cover */}
                                                        <div className="pd-slide-cover-stage mb-3 p-3 text-center rounded-3" style={{ background: '#080b1a', border: '1px solid rgba(255, 255, 255, 0.06)' }}>
                                                            <SoftwareBoxMockup 
                                                                product={item} 
                                                                lang={lang} 
                                                                onClick={() => navigate(itemUrl)}
                                                            />
                                                        </div>

                                                        <div className="mt-2">
                                                            <span className="pd-card-cat-badge">
                                                                {lang === 'ka' ? (item.category_name_ka || 'პლატფორმა') : (item.category_name_en || 'Platform')}
                                                            </span>
                                                            <h4 className="pd-card-title mb-1">
                                                                <Link to={itemUrl} title={itemName}>{itemName}</Link>
                                                            </h4>
                                                            {itemTagline && (
                                                                <p className="pd-card-desc mb-3">{itemTagline}</p>
                                                            )}
                                                        </div>

                                                        <div className="pd-card-price-row mt-auto pt-3 border-top border-secondary border-opacity-25 d-flex align-items-center justify-content-between gap-2">
                                                            <div>
                                                                <span className="pd-price-lbl">{lang === 'ka' ? 'საწყისი' : 'Setup'}</span>
                                                                <div className="fw-bold text-info">
                                                                    {parseFloat(item.setup_price) > 0 ? `₾${parseFloat(item.setup_price).toLocaleString()}` : (lang === 'ka' ? 'მოთხოვნით' : 'Quote')}
                                                                </div>
                                                            </div>
                                                            <div className="d-flex align-items-center gap-2">
                                                                <Link 
                                                                    to={`/${lang}/contact?product=${encodeURIComponent(itemName)}`} 
                                                                    className="pd-card-demo-btn"
                                                                    title={lang === 'ka' ? 'დემო / შეკვეთა' : 'Request Demo / Quote'}
                                                                >
                                                                    <i className="fas fa-paper-plane me-1" />
                                                                    <span>{lang === 'ka' ? 'დემო' : 'Demo'}</span>
                                                                </Link>
                                                                <Link to={itemUrl} className="pd-card-view-btn" title={itemName}>
                                                                    <span>{lang === 'ka' ? 'ნახვა' : 'View'}</span>
                                                                    <i className="fas fa-arrow-right" />
                                                                </Link>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </main>
            </Layout>

            <style>{`
                .pd-breadcrumbs {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    font-size: 13px;
                    color: rgba(255, 255, 255, 0.5);
                    flex-wrap: wrap;
                }
                .pd-breadcrumbs a {
                    color: rgba(255, 255, 255, 0.7);
                    text-decoration: none;
                    transition: color 0.2s;
                }
                .pd-breadcrumbs a:hover {
                    color: #a29bfe;
                }
                .pd-sep {
                    color: rgba(255, 255, 255, 0.25);
                }
                .pd-current {
                    color: #ffffff;
                    font-weight: 600;
                }
                .pd-hero-banner {
                    background: rgba(14, 19, 44, 0.85);
                    backdrop-filter: blur(14px);
                    border: 1px solid rgba(255, 255, 255, 0.08);
                    border-radius: 24px;
                    padding: 36px 32px;
                    box-shadow: 0 20px 50px rgba(0, 0, 0, 0.4);
                }
                .pd-icon-box {
                    width: 64px;
                    height: 64px;
                    border-radius: 18px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 32px;
                    border: 1px solid;
                    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.3);
                }
                .pd-badge-category {
                    font-size: 11px;
                    font-weight: 700;
                    text-transform: uppercase;
                    letter-spacing: 1px;
                    padding: 4px 12px;
                    border-radius: 20px;
                    background: rgba(108, 92, 231, 0.2);
                    color: #a29bfe;
                    border: 1px solid rgba(108, 92, 231, 0.35);
                }
                .pd-badge-market {
                    font-size: 11px;
                    font-weight: 600;
                    padding: 4px 12px;
                    border-radius: 20px;
                    background: rgba(255, 255, 255, 0.06);
                    color: rgba(255, 255, 255, 0.8);
                    border: 1px solid rgba(255, 255, 255, 0.1);
                }
                .pd-badge-featured {
                    font-size: 11px;
                    font-weight: 700;
                    padding: 4px 12px;
                    border-radius: 20px;
                    background: rgba(245, 158, 11, 0.2);
                    color: #fbbf24;
                    border: 1px solid rgba(245, 158, 11, 0.35);
                }
                .pd-title {
                    font-size: clamp(1.8rem, 3.5vw, 2.6rem);
                    line-height: 1.2;
                }
                .pd-tagline {
                    color: rgba(255, 255, 255, 0.7);
                    font-size: 16px;
                    line-height: 1.6;
                    margin: 0;
                    max-width: 720px;
                }
                .pd-btn-share {
                    background: rgba(255, 255, 255, 0.06);
                    border: 1px solid rgba(255, 255, 255, 0.12);
                    color: #ffffff;
                    padding: 10px 20px;
                    border-radius: 50px;
                    font-size: 13px;
                    font-weight: 600;
                    cursor: pointer;
                    transition: all 0.2s;
                }
                .pd-btn-share:hover {
                    background: rgba(255, 255, 255, 0.15);
                }
                .pd-btn-live-demo {
                    background: linear-gradient(135deg, #10b981, #059669);
                    color: #ffffff;
                    padding: 10px 22px;
                    border-radius: 50px;
                    font-size: 13px;
                    font-weight: 700;
                    text-decoration: none;
                    display: inline-flex;
                    align-items: center;
                    transition: all 0.2s;
                    box-shadow: 0 4px 15px rgba(16, 185, 129, 0.3);
                }
                .pd-btn-live-demo:hover {
                    transform: translateY(-2px);
                    color: #fff;
                    box-shadow: 0 6px 20px rgba(16, 185, 129, 0.45);
                }
                .pd-content-card {
                    background: rgba(14, 19, 44, 0.75);
                    backdrop-filter: blur(12px);
                    border: 1px solid rgba(255, 255, 255, 0.08);
                    border-radius: 20px;
                    padding: 32px 28px;
                }
                .pd-section-heading {
                    color: #ffffff;
                    font-size: 1.25rem;
                    font-weight: 700;
                    margin-bottom: 20px;
                    display: flex;
                    align-items: center;
                }
                .pd-description p {
                    color: rgba(255, 255, 255, 0.75);
                    font-size: 15px;
                    line-height: 1.8;
                    margin: 0;
                }
                .pd-features-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
                    gap: 14px;
                }
                .pd-feature-box {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    padding: 14px 16px;
                    border-radius: 14px;
                    background: rgba(255, 255, 255, 0.03);
                    border: 1px solid rgba(255, 255, 255, 0.06);
                }
                .pd-feature-check {
                    width: 32px;
                    height: 32px;
                    border-radius: 8px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 14px;
                    flex-shrink: 0;
                }
                .pd-feature-text {
                    color: #ffffff;
                    font-size: 14px;
                    font-weight: 500;
                }
                .pd-tech-tags {
                    display: flex;
                    flex-wrap: wrap;
                    gap: 10px;
                }
                .pd-tech-pill {
                    display: inline-flex;
                    align-items: center;
                    padding: 8px 16px;
                    border-radius: 50px;
                    background: rgba(255, 255, 255, 0.05);
                    border: 1px solid rgba(255, 255, 255, 0.1);
                    color: #ffffff;
                    font-size: 13px;
                    font-weight: 600;
                }
                .pd-assurance-item {
                    display: flex;
                    gap: 14px;
                    padding: 18px;
                    border-radius: 14px;
                    background: rgba(255, 255, 255, 0.02);
                    border: 1px solid rgba(255, 255, 255, 0.05);
                    height: 100%;
                }
                .pd-assurance-icon {
                    font-size: 24px;
                }
                .pd-assurance-item strong {
                    display: block;
                    color: #fff;
                    font-size: 14px;
                    margin-bottom: 6px;
                }
                .pd-assurance-item p {
                    color: rgba(255, 255, 255, 0.55);
                    font-size: 12px;
                    line-height: 1.6;
                    margin: 0;
                }
                .pd-sidebar-sticky {
                    position: sticky;
                    top: 100px;
                }
                .pd-pricing-card {
                    background: rgba(14, 19, 44, 0.9);
                    backdrop-filter: blur(14px);
                    border: 1px solid rgba(255, 255, 255, 0.12);
                    border-radius: 22px;
                    padding: 28px 24px;
                    box-shadow: 0 15px 40px rgba(0, 0, 0, 0.35);
                }
                .pd-pricing-badge {
                    display: inline-block;
                    font-size: 11px;
                    font-weight: 700;
                    text-transform: uppercase;
                    letter-spacing: 1px;
                    color: #a29bfe;
                    background: rgba(108, 92, 231, 0.15);
                    border: 1px solid rgba(108, 92, 231, 0.3);
                    padding: 4px 12px;
                    border-radius: 20px;
                    margin-bottom: 18px;
                }
                .pd-price-type {
                    display: block;
                    font-size: 12px;
                    color: rgba(255, 255, 255, 0.5);
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                    margin-bottom: 4px;
                }
                .pd-price-large {
                    color: #ffffff;
                    font-size: 2.2rem;
                    font-weight: 800;
                    line-height: 1.1;
                }
                .pd-currency {
                    color: #6c5ce7;
                    font-size: 1.6rem;
                    margin-right: 4px;
                }
                .pd-price-quote {
                    font-size: 1.5rem;
                    color: #38bdf8;
                }
                .pd-price-sub-row {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    padding: 8px 12px;
                    background: rgba(255, 255, 255, 0.03);
                    border-radius: 8px;
                }
                .pd-divider {
                    height: 1px;
                    background: rgba(255, 255, 255, 0.08);
                }
                .pd-btn-order {
                    display: block;
                    background: linear-gradient(135deg, #6c5ce7, #4f46e5);
                    color: #ffffff;
                    font-weight: 700;
                    font-size: 15px;
                    padding: 14px 20px;
                    border-radius: 12px;
                    text-decoration: none;
                    transition: all 0.3s;
                    box-shadow: 0 8px 25px rgba(108, 92, 231, 0.4);
                }
                .pd-btn-order:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 12px 30px rgba(108, 92, 231, 0.6);
                    color: #fff;
                }
                .pd-btn-docs {
                    display: block;
                    background: rgba(255, 255, 255, 0.05);
                    border: 1px solid rgba(255, 255, 255, 0.1);
                    color: rgba(255, 255, 255, 0.85);
                    font-weight: 600;
                    font-size: 13px;
                    padding: 10px 16px;
                    border-radius: 10px;
                    text-decoration: none;
                    transition: all 0.2s;
                }
                .pd-btn-docs:hover {
                    background: rgba(255, 255, 255, 0.1);
                    color: #fff;
                }
                .pd-guarantee-list {
                    display: flex;
                    flex-direction: column;
                    gap: 8px;
                    margin-top: 16px;
                }
                .pd-g-item {
                    font-size: 12px;
                    color: rgba(255, 255, 255, 0.65);
                    display: flex;
                    align-items: center;
                }
                .pd-related-card {
                    background: rgba(14, 19, 44, 0.8);
                    border: 1px solid rgba(255, 255, 255, 0.08);
                    border-radius: 20px;
                    padding: 22px 20px;
                }
                .pd-related-title {
                    font-size: 14px;
                    font-weight: 700;
                    color: #ffffff;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                }
                .pd-related-item {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    padding: 10px 12px;
                    border-radius: 12px;
                    background: rgba(255, 255, 255, 0.03);
                    text-decoration: none;
                    transition: all 0.2s;
                }
                .pd-related-item:hover {
                    background: rgba(255, 255, 255, 0.08);
                    transform: translateX(4px);
                }
                .pd-related-icon {
                    font-size: 22px;
                }
                .pd-related-name {
                    font-size: 13px;
                    font-weight: 600;
                    color: #ffffff;
                }

                /* Explore Catalog Section Styles */
                .pd-explore-sub {
                    font-size: 12px;
                    font-weight: 700;
                    letter-spacing: 1.5px;
                    color: #a29bfe;
                }
                .pd-explore-title {
                    font-size: clamp(1.4rem, 2.5vw, 1.8rem);
                    font-weight: 800;
                }
                .pd-view-toggle {
                    display: inline-flex;
                    background: rgba(255, 255, 255, 0.04);
                    border: 1px solid rgba(255, 255, 255, 0.08);
                    border-radius: 12px;
                    padding: 4px;
                    gap: 4px;
                }
                .pd-toggle-btn {
                    border: none;
                    background: transparent;
                    color: rgba(255, 255, 255, 0.6);
                    padding: 6px 12px;
                    border-radius: 8px;
                    font-size: 14px;
                    cursor: pointer;
                    transition: all 0.2s;
                }
                .pd-toggle-btn.active {
                    background: #6c5ce7;
                    color: #ffffff;
                    box-shadow: 0 4px 12px rgba(108, 92, 231, 0.4);
                }
                .pd-sort-select {
                    background: rgba(14, 19, 44, 0.8);
                    color: #ffffff;
                    border: 1px solid rgba(255, 255, 255, 0.12);
                    padding: 8px 14px;
                    border-radius: 12px;
                    font-size: 13px;
                    outline: none;
                    cursor: pointer;
                }
                .pd-sort-select option {
                    background: #0d122b;
                    color: #ffffff;
                }
                .pd-all-catalog-btn {
                    display: inline-flex;
                    align-items: center;
                    background: rgba(255, 255, 255, 0.06);
                    border: 1px solid rgba(255, 255, 255, 0.12);
                    color: #ffffff;
                    padding: 8px 16px;
                    border-radius: 12px;
                    font-size: 13px;
                    font-weight: 600;
                    text-decoration: none;
                    transition: all 0.2s;
                }
                .pd-all-catalog-btn:hover {
                    background: rgba(108, 92, 231, 0.3);
                    border-color: #6c5ce7;
                    color: #ffffff;
                }
                .pd-cat-filter-tabs {
                    display: flex;
                    flex-wrap: wrap;
                    gap: 8px;
                }
                .pd-filter-tab {
                    background: rgba(255, 255, 255, 0.04);
                    border: 1px solid rgba(255, 255, 255, 0.08);
                    color: rgba(255, 255, 255, 0.75);
                    padding: 6px 14px;
                    border-radius: 20px;
                    font-size: 12px;
                    font-weight: 600;
                    cursor: pointer;
                    transition: all 0.2s;
                }
                .pd-filter-tab:hover {
                    background: rgba(255, 255, 255, 0.08);
                    color: #ffffff;
                }
                .pd-filter-tab.active {
                    background: linear-gradient(135deg, #6c5ce7, #4f46e5);
                    border-color: transparent;
                    color: #ffffff;
                    box-shadow: 0 4px 15px rgba(108, 92, 231, 0.35);
                }
                .pd-card-item {
                    background: rgba(14, 19, 44, 0.7);
                    backdrop-filter: blur(12px);
                    border: 1px solid rgba(255, 255, 255, 0.08);
                    border-radius: 18px;
                    padding: 24px;
                    display: flex;
                    flex-direction: column;
                    height: 100%;
                    transition: all 0.3s;
                }
                .pd-card-item:hover {
                    transform: translateY(-6px);
                    border-color: rgba(255, 255, 255, 0.2);
                    box-shadow: 0 16px 40px rgba(0, 0, 0, 0.4);
                }
                .pd-card-icon {
                    width: 48px;
                    height: 48px;
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 24px;
                    border: 1px solid;
                }
                .pd-card-market {
                    font-size: 11px;
                    color: rgba(255, 255, 255, 0.5);
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                    font-weight: 600;
                }
                .pd-card-cat-badge {
                    font-size: 11px;
                    font-weight: 700;
                    color: #a29bfe;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                    display: inline-block;
                    margin-bottom: 6px;
                }
                .pd-card-title {
                    font-size: 1.1rem;
                    font-weight: 700;
                    line-height: 1.4;
                }
                .pd-card-title a {
                    color: #ffffff;
                    text-decoration: none;
                    transition: color 0.2s;
                }
                .pd-card-title a:hover {
                    color: #a29bfe;
                }
                .pd-card-desc {
                    font-size: 13px;
                    color: rgba(255, 255, 255, 0.65);
                    line-height: 1.5;
                    display: -webkit-box;
                    -webkit-line-clamp: 2;
                    -webkit-box-orient: vertical;
                    overflow: hidden;
                }
                .pd-price-lbl {
                    font-size: 10px;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                    color: rgba(255, 255, 255, 0.45);
                    display: block;
                }
                .pd-card-view-btn {
                    display: inline-flex;
                    align-items: center;
                    gap: 6px;
                    background: rgba(255, 255, 255, 0.06);
                    border: 1px solid rgba(255, 255, 255, 0.12);
                    color: #ffffff;
                    font-size: 12px;
                    font-weight: 600;
                    padding: 6px 14px;
                    border-radius: 8px;
                    text-decoration: none;
                    transition: all 0.2s;
                }
                .pd-card-view-btn:hover {
                    background: #6c5ce7;
                    border-color: #6c5ce7;
                    color: #ffffff;
                    transform: translateX(2px);
                }

                /* ── Catalog Slider Styles ── */
                .pd-slider-nav-btn {
                    width: 34px;
                    height: 34px;
                    border-radius: 8px;
                    background: rgba(255, 255, 255, 0.05);
                    border: 1px solid rgba(255, 255, 255, 0.12);
                    color: #ffffff;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 12px;
                    cursor: pointer;
                    transition: all 0.2s ease;
                }
                .pd-slider-nav-btn:hover {
                    background: #6366f1;
                    border-color: #6366f1;
                    color: #ffffff;
                    transform: scale(1.05);
                }
                .pd-slider-wrapper {
                    position: relative;
                    width: 100%;
                }
                .pd-slider-track {
                    display: flex;
                    gap: 20px;
                    overflow-x: auto;
                    scroll-behavior: smooth;
                    scroll-snap-type: x mandatory;
                    scrollbar-width: none;
                    -ms-overflow-style: none;
                    padding: 8px 4px 24px;
                }
                .pd-slider-track::-webkit-scrollbar {
                    display: none;
                }
                .pd-slide-item {
                    flex: 0 0 calc(33.333% - 14px);
                    min-width: 290px;
                    scroll-snap-align: start;
                    box-sizing: border-box;
                }
                @media (max-width: 991px) {
                    .pd-slide-item {
                        flex: 0 0 calc(50% - 10px);
                    }
                }
                @media (max-width: 576px) {
                    .pd-slide-item {
                        flex: 0 0 100%;
                    }
                }
                .pd-card-demo-btn {
                    display: inline-flex;
                    align-items: center;
                    gap: 5px;
                    background: rgba(99, 102, 241, 0.15);
                    border: 1px solid rgba(99, 102, 241, 0.35);
                    color: #c7d2fe;
                    font-size: 11px;
                    font-weight: 600;
                    padding: 6px 12px;
                    border-radius: 8px;
                    text-decoration: none;
                    transition: all 0.2s ease;
                }
                .pd-card-demo-btn:hover {
                    background: #6366f1;
                    color: #ffffff;
                    border-color: #6366f1;
                }
            `}</style>
        </Fragment>
    );
};

export default ProductDetails;
